/**
 * Thẻ "Ảnh bìa và logo" ở Cấu hình → Thông tin tiệm (v0.23.3, ADR-124; docs/plans/thuong-hieu-cua-hang-banner-logo-mau-nganh.md 4.5).
 * CHỈ CHỦ (do `CauHinh.tsx` chỉ vẽ trong khối của chủ) và CHỈ khi máy chủ báo `toi.anhCuaHang.bat === true` — máy chủ cũ không trả trường này,
 * cờ `ANH_CUA_HANG_BAT` tắt, hoặc thiếu kho ảnh → thẻ không có. Ảnh lưu NGAY khi bấm "Dùng ảnh này" (không có nút Lưu chung) nên là thẻ riêng.
 *
 * Chưa có ảnh nào → thu gọn: một câu + nút "Thêm ảnh bìa và logo". Mở ra (hoặc đã có ảnh) → hai hàng Ảnh bìa / Logo, mỗi hàng "Chọn ảnh" hoặc
 * "Đổi ảnh" + "Gỡ". Chọn ảnh → máy quầy tự cắt / đặt vừa (anh-cua-hang.ts) → tờ xem trước ĐÚNG ảnh sẽ lưu → "Dùng ảnh này".
 * Mọi lỗi hiện NGAY TRONG thẻ / tờ (bài học ảnh khách: thông báo cuối trang bị nút chân tờ che ở điện thoại).
 * Vòng sửa 1/2 sau giám khảo (30/10/2026): lỗi / báo "đã lưu, đã gỡ" nằm NGAY DƯỚI HÀNG vừa bấm (không dồn xuống cuối thẻ — ở 320×640 / 360×640
 * cuối thẻ nằm ngoài màn, dưới thanh dưới), lỗi `role="alert"`, tự cuộn vào tầm nhìn (chừa thanh dưới); báo thành công cũng nằm trong thẻ (tự tắt sau
 * 3 giây) chứ không phải thông báo nổi của Cấu hình, vì thông báo nổi che chữ của chính thẻ.
 */
import { X } from 'lucide-react'
import { type ChangeEvent, type ReactNode, useEffect, useRef, useState } from 'react'
import {
  type AnhDaChuanBi,
  cauLoi,
  chuanBiAnh,
  chuCaiLogo,
  duongDanAnhHopLe,
  type LoaiAnh,
} from './anh-cua-hang.ts'
import { anhCuaHangApi, duongDanMayChu, type ToiLaAi } from './api.ts'
import { TA } from './text/anh-cua-hang.ts'
import { ThongBao, To } from './ui.tsx'

/** Tờ xem trước đúng ảnh sẽ lưu. Trình bày thuần (không tự gọi máy chủ) để test vẽ ra chữ / nút được. */
export function ToXemTruocAnh({
  loai,
  url,
  dang,
  loi,
  onDung,
  onChonKhac,
  onDong,
}: {
  loai: LoaiAnh
  url: string
  /** Đang gửi ảnh lên: khoá cả hai nút + nút đóng, tờ GIỮ MỞ tới khi xong (bài học G-N1 v0.23.1). */
  dang: boolean
  /** Lỗi của lần gửi vừa rồi (câu tiếng Việt) — hiện ngay dưới ảnh, tờ không đóng. */
  loi: string | null
  onDung: () => void
  onChonKhac: () => void
  onDong: () => void
}) {
  const tieuDe = loai === 'bia' ? TA.tieuDeToBia : TA.tieuDeToLogo
  return (
    <To nhan={tieuDe} onDong={dang ? () => undefined : onDong}>
      <header className="to-dau">
        <h2>{tieuDe}</h2>
        <button
          type="button"
          className="nut nut-nhat nut-icon"
          aria-label={TA.dongTo}
          disabled={dang}
          onClick={onDong}
        >
          <X size={22} />
        </button>
      </header>
      <div className="to-than">
        <div className={`ta-khung ${loai === 'bia' ? 'ta-khung-bia' : 'ta-khung-logo'}`}>
          <img
            className="ta-anh"
            src={url}
            alt={loai === 'bia' ? TA.anhXemTruocBia : TA.anhXemTruocLogo}
          />
        </div>
        <p className="chu-phu ta-cau">{TA.cauToXemTruoc}</p>
        {loi !== null && (
          <ThongBao loai="loi" khan>
            {loi}
          </ThongBao>
        )}
      </div>
      <footer className="to-chan ta-chan">
        <button type="button" className="nut nut-vien" disabled={dang} onClick={onChonKhac}>
          {TA.chonKhac}
        </button>
        <button
          type="button"
          className={dang ? 'nut nut-chinh ta-dang-luu' : 'nut nut-chinh'}
          disabled={dang}
          onClick={onDung}
        >
          {dang ? TA.dangLuu : TA.dung}
        </button>
      </footer>
    </To>
  )
}

/** Ảnh nhỏ của một hàng: ảnh thật nếu có và tải được, không thì dải màu ngành (bìa) / chữ cái đầu tên tiệm (logo). */
function AnhNho({
  loai,
  duong,
  tenTiem,
}: {
  loai: LoaiAnh
  duong: string | null
  tenTiem: string
}) {
  const [hong, setHong] = useState(false)
  // Đổi ảnh (đường dẫn mới) thì thử tải lại; ảnh cũ hỏng không được "dính" sang ảnh mới.
  // biome-ignore lint/correctness/useExhaustiveDependencies: chạy lại đúng khi đổi `duong`.
  useEffect(() => setHong(false), [duong])
  return (
    <span className={`ta-thu ${loai === 'bia' ? 'ta-thu-bia' : 'ta-thu-logo'}`} aria-hidden="true">
      {duong !== null && !hong ? (
        <img src={duongDanMayChu(duong)} alt="" onError={() => setHong(true)} />
      ) : loai === 'logo' ? (
        chuCaiLogo(tenTiem)
      ) : null}
    </span>
  )
}

/** Báo của MỘT hàng (lỗi hoặc "đã lưu / đã gỡ"). `lan` tăng mỗi lần báo để cùng một câu hiện lại vẫn cuộn vào màn. */
export type BaoHang = { kieu: 'loi' | 'thanh-cong'; cau: string; lan: number }

/** Khối báo trong hàng: lỗi `role="alert"`; hiện ra thì cuộn vào tầm nhìn (CSS `.ta-bao` chừa thanh dưới của điện thoại). */
function KhoiBao({ bao }: { bao: BaoHang }) {
  const goc = useRef<HTMLDivElement>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: chạy lại đúng mỗi lần có báo mới (`lan`).
  useEffect(() => {
    goc.current?.scrollIntoView({ block: 'nearest' })
  }, [bao.lan])
  return (
    <div className="ta-bao" ref={goc}>
      <ThongBao loai={bao.kieu} khan={bao.kieu === 'loi'}>
        {bao.cau}
      </ThongBao>
    </div>
  )
}

/**
 * Một hàng (Ảnh bìa / Logo): ảnh nhỏ + tên, nút "Chọn ảnh" hoặc "Đổi ảnh" + "Gỡ", hộp hỏi gỡ, và báo của chính hàng này NGAY DƯỚI nút vừa bấm
 * (khi đang hỏi gỡ thì báo nằm trong hộp, sát hai nút). Trình bày thuần để test vẽ ra HTML được.
 */
export function HangAnh({
  loai,
  duong,
  tenTiem,
  dangBan,
  dangChuanBi,
  hoiGo,
  dangGo,
  bao,
  onChon,
  onHoiGo,
  onGiuLai,
  onGo,
}: {
  loai: LoaiAnh
  duong: string | null
  tenTiem: string
  /** Có việc đang chạy ở đâu đó trong thẻ: khoá mọi nút. */
  dangBan: boolean
  /** Hàng này đang đọc / nén ảnh vừa chọn. */
  dangChuanBi: boolean
  hoiGo: boolean
  dangGo: boolean
  bao: BaoHang | null
  onChon: () => void
  onHoiGo: () => void
  onGiuLai: () => void
  onGo: () => void
}) {
  const laBia = loai === 'bia'
  const khoiBao = bao === null ? null : <KhoiBao key={bao.lan} bao={bao} />
  return (
    <div className="ta-hang">
      {/* Chỉ "ảnh nhỏ + chữ + nút" là hàng ngang ở tablet; hộp hỏi gỡ và báo là con của `.ta-hang` (luôn xếp cột, chiếm trọn hàng) — N-GD5. */}
      <div className="ta-hang-tren">
        <div className="ta-hang-dau">
          <AnhNho loai={loai} duong={duong} tenTiem={tenTiem} />
          <div className="ta-chu">
            <b>{laBia ? TA.nhanBia : TA.nhanLogo}</b>
            <span className="chu-phu">
              {duong !== null ? TA.coAnh : laBia ? TA.chuaCoBia : TA.chuaCoLogo}
            </span>
          </div>
        </div>
        <NutHang
          laBia={laBia}
          coAnh={duong !== null}
          dangBan={dangBan}
          dangChuanBi={dangChuanBi}
          onChon={onChon}
          onHoiGo={onHoiGo}
        />
      </div>
      {!hoiGo && khoiBao}
      {hoiGo && (
        <HopHoiGo laBia={laBia} dangGo={dangGo} onGiuLai={onGiuLai} onGo={onGo}>
          {khoiBao}
        </HopHoiGo>
      )}
      {laBia && <p className="chu-phu">{TA.danBia}</p>}
    </div>
  )
}

/** Cụm nút của hàng: chưa có ảnh → "Chọn ảnh"; có ảnh → "Đổi ảnh" + "Gỡ" (viền + chữ đỏ nhạt, vẫn một chạm rồi có hộp hỏi). */
function NutHang({
  laBia,
  coAnh,
  dangBan,
  dangChuanBi,
  onChon,
  onHoiGo,
}: {
  laBia: boolean
  coAnh: boolean
  dangBan: boolean
  dangChuanBi: boolean
  onChon: () => void
  onHoiGo: () => void
}) {
  if (!coAnh) {
    return (
      <div className="ta-nut">
        <button
          type="button"
          className="nut nut-vien"
          aria-label={laBia ? TA.chonBia : TA.chonLogo}
          disabled={dangBan}
          onClick={onChon}
        >
          {dangChuanBi ? TA.dangChuanBi : TA.chon}
        </button>
      </div>
    )
  }
  return (
    <div className="ta-nut">
      <button
        type="button"
        className="nut nut-vien"
        aria-label={laBia ? TA.doiBia : TA.doiLogo}
        disabled={dangBan}
        onClick={onChon}
      >
        {dangChuanBi ? TA.dangChuanBi : TA.doi}
      </button>
      <button
        type="button"
        className="nut nut-nguy"
        aria-label={laBia ? TA.goBia : TA.goLogo}
        disabled={dangBan}
        onClick={onHoiGo}
      >
        {TA.go}
      </button>
    </div>
  )
}

/** Hộp hỏi gỡ: "Giữ lại" là nút nổi (chính), "Gỡ ảnh" là nút nguy hiểm, cách nhau ≥ 24px (gỡ có hiệu lực ngay). */
function HopHoiGo({
  laBia,
  dangGo,
  onGiuLai,
  onGo,
  children,
}: {
  laBia: boolean
  dangGo: boolean
  onGiuLai: () => void
  onGo: () => void
  /** Báo lỗi của lần gỡ vừa rồi, nằm giữa câu hỏi và hai nút. */
  children: ReactNode
}) {
  return (
    <div className="ta-hoi-go" role="alertdialog" aria-label={TA.xacNhanGo}>
      <p>{laBia ? TA.hoiGoBia : TA.hoiGoLogo}</p>
      {children}
      <div className="hang-phu">
        <button type="button" className="nut nut-chinh" disabled={dangGo} onClick={onGiuLai}>
          {TA.giuLai}
        </button>
        <button type="button" className="nut nut-nguy" disabled={dangGo} onClick={onGo}>
          {dangGo ? TA.dangGo : TA.xacNhanGo}
        </button>
      </div>
    </div>
  )
}

export function TheAnhCuaHang({
  toi,
  onDaLuu,
}: {
  toi: ToiLaAi
  /** Tải lại `toi` (đường dẫn ảnh mới) — giống thẻ Thông tin tiệm. */
  onDaLuu: () => void
}) {
  const cfg = toi.anhCuaHang
  const bia = duongDanAnhHopLe(cfg?.bia)
  const logo = duongDanAnhHopLe(cfg?.logo)
  const [mo, setMo] = useState(bia !== null || logo !== null)
  const [dangXuLy, setDangXuLy] = useState<LoaiAnh | null>(null)
  const [tam, setTam] = useState<AnhDaChuanBi | null>(null)
  const [dangLuu, setDangLuu] = useState(false)
  const [loiTo, setLoiTo] = useState<string | null>(null)
  // Báo của từng hàng: chỉ MỘT báo một lúc, gắn với hàng vừa bấm (`loai`).
  const [bao, setBao] = useState<(BaoHang & { loai: LoaiAnh }) | null>(null)
  const [hoiGo, setHoiGo] = useState<LoaiAnh | null>(null)
  const [dangGo, setDangGo] = useState(false)
  const oTep = useRef<HTMLInputElement>(null)
  const loaiChon = useRef<LoaiAnh>('bia')
  const tamRef = useRef<AnhDaChuanBi | null>(null)
  const lanBao = useRef(0)

  function datBao(loai: LoaiAnh, kieu: BaoHang['kieu'], cau: string) {
    lanBao.current += 1
    setBao({ loai, kieu, cau, lan: lanBao.current })
  }

  // "Đã lưu / đã gỡ" tự tắt sau 3 giây (lỗi thì ở lại tới lần bấm kế tiếp).
  useEffect(() => {
    if (bao === null || bao.kieu !== 'thanh-cong') return
    const t = setTimeout(() => setBao(null), 3000)
    return () => clearTimeout(t)
  }, [bao])

  // Địa chỉ `blob:` của ảnh xem trước phải được trả lại khi bỏ / đóng tờ / rời trang (rò bộ nhớ trên tablet cũ).
  useEffect(() => {
    tamRef.current = tam
  }, [tam])
  useEffect(
    () => () => {
      if (tamRef.current !== null) URL.revokeObjectURL(tamRef.current.xemTruoc)
    },
    [],
  )

  if (cfg?.bat !== true) return null

  const dangBan = dangXuLy !== null || dangLuu || dangGo

  function bo() {
    if (tam !== null) URL.revokeObjectURL(tam.xemTruoc)
    setTam(null)
    setLoiTo(null)
  }

  function chon(loai: LoaiAnh) {
    loaiChon.current = loai
    setBao(null)
    setHoiGo(null)
    oTep.current?.click()
  }

  async function khiChonTep(e: ChangeEvent<HTMLInputElement>) {
    const tep = e.target.files?.[0]
    // Xoá giá trị để chọn LẠI đúng tệp đó vẫn kích hoạt `change`.
    e.target.value = ''
    if (tep === undefined) return
    const loai = loaiChon.current
    setDangXuLy(loai)
    setBao(null)
    try {
      const a = await chuanBiAnh(tep, loai)
      if (tam !== null) URL.revokeObjectURL(tam.xemTruoc)
      setLoiTo(null)
      setTam(a)
    } catch (err) {
      datBao(loai, 'loi', cauLoi(err, 'tai'))
    } finally {
      setDangXuLy(null)
    }
  }

  async function dung() {
    if (tam === null || dangLuu) return
    setDangLuu(true)
    setLoiTo(null)
    try {
      await anhCuaHangApi.tai(tam.loai, tam.base64)
      const loai = tam.loai
      bo()
      setMo(true)
      datBao(loai, 'thanh-cong', loai === 'bia' ? TA.daLuuBia : TA.daLuuLogo)
      onDaLuu()
    } catch (err) {
      // Tờ giữ mở, lỗi hiện ngay dưới ảnh (không đưa lên thông báo cuối trang: nút chân tờ che mất).
      setLoiTo(cauLoi(err, 'tai'))
    } finally {
      setDangLuu(false)
    }
  }

  function chonKhac() {
    const loai = tam?.loai ?? 'bia'
    bo()
    chon(loai)
  }

  async function go(loai: LoaiAnh) {
    if (dangGo) return
    setDangGo(true)
    setBao(null)
    try {
      await anhCuaHangApi.go(loai)
      setHoiGo(null)
      datBao(loai, 'thanh-cong', loai === 'bia' ? TA.daGoBia : TA.daGoLogo)
      onDaLuu()
    } catch (err) {
      datBao(loai, 'loi', cauLoi(err, 'go'))
    } finally {
      setDangGo(false)
    }
  }

  const hang = (loai: LoaiAnh, duong: string | null) => (
    <HangAnh
      loai={loai}
      duong={duong}
      tenTiem={toi.tenTiem}
      dangBan={dangBan}
      dangChuanBi={dangXuLy === loai}
      hoiGo={hoiGo === loai}
      dangGo={dangGo}
      bao={bao?.loai === loai ? bao : null}
      onChon={() => chon(loai)}
      onHoiGo={() => {
        setBao(null)
        setHoiGo(loai)
      }}
      onGiuLai={() => setHoiGo(null)}
      onGo={() => void go(loai)}
    />
  )

  return (
    <section className="the the-dem the-khoi the-anh-cua-hang">
      <h2>{TA.tieuDe}</h2>
      <p className="chu-phu">{TA.goiY}</p>
      <input
        ref={oTep}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/*"
        hidden
        onChange={(e) => void khiChonTep(e)}
      />
      {!mo ? (
        <div className="ta-nut">
          <button type="button" className="nut nut-vien" onClick={() => setMo(true)}>
            {TA.nutThem}
          </button>
        </div>
      ) : (
        <>
          {hang('bia', bia)}
          {hang('logo', logo)}
          <p className="chu-phu">{TA.nhacQuyen}</p>
        </>
      )}
      {tam !== null && (
        <ToXemTruocAnh
          loai={tam.loai}
          url={tam.xemTruoc}
          dang={dangLuu}
          loi={loiTo}
          onDung={() => void dung()}
          onChonKhac={chonKhac}
          onDong={bo}
        />
      )}
    </section>
  )
}
